"use client";

/**
 * ObservabilityErrorBoundary — catches React-tree render errors.
 *
 * Sits at the innermost level of the provider tree (see
 * ``plans/PLAN_POSTHOG_01_FOUNDATIONS.md`` § "Provider architecture")
 * so it catches errors in the actual page tree without swallowing
 * provider-init errors — which would leave the app non-functional
 * anyway.
 *
 * On catch the boundary fires TWO things (Plan_03 § "ErrorBoundary"):
 *
 *   1. ``posthog.captureException(error, {extra: ...})`` → PostHog
 *      Exceptions feature (dedicated UI, source-map decoding,
 *      grouping). Carries ``componentStack`` from React's
 *      ``errorInfo`` so the failing component is identifiable even
 *      after the boundary unwinds it.
 *
 *   2. ``captureEvent({ event_name: "platform.error.surfaced", ... })``
 *      → regular Event timeline. Correlatable with the preceding user
 *      actions (period-picker change, country-filter click) that
 *      led to the failure. Plan_02 § "platform.error.surfaced" pins
 *      the schema.
 *
 * Why both: the Exceptions feature is great for grouped-by-stack
 * debugging; the Event timeline is great for funnel correlation
 * (what was the user doing when the boundary fired). The two views
 * answer different questions.
 *
 * The fallback UI is ``TileError`` (existing) rendered inside an
 * adapter wrapper — no changes to ``TileError`` itself. Same
 * grammar as the tile-level boundaries elsewhere in the app, so the
 * user always sees the same "this tile is still warming up" affordance
 * whether the failure was a network 500 or a render-time throw.
 *
 * Why a class component: React error boundaries require the legacy
 * class API. The ``react-error-boundary`` library wraps this same
 * mechanism but adds a dependency for one component.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import posthog from "posthog-js";

import { captureEvent, standardBody } from "@/lib/analytics";
import { TileError } from "@/components/ui/tile-error";

/** ``error_message`` cap per Plan_03 § "What goes into the exception payload". */
const ERROR_MESSAGE_MAX_LENGTH = 500;

function truncateMessage(message: string): string {
  if (message.length <= ERROR_MESSAGE_MAX_LENGTH) return message;
  return message.slice(0, ERROR_MESSAGE_MAX_LENGTH - 1) + "…";
}

/**
 * Derive ``screen`` from the current URL. We don't read the auth
 * context from a class component (no hooks), and we deliberately
 * keep this dependency-free so the boundary fires even when the
 * other providers have themselves errored out.
 *
 * Strategy: take the first two non-empty path segments
 * (``/assets/AAPL`` → ``"assets/AAPL"``, ``/dashboard`` → ``"dashboard"``).
 * Falls back to ``"unknown"`` if the URL is empty or SSR.
 */
function deriveScreen(): string {
  if (typeof window === "undefined") return "unknown";
  const path = window.location.pathname ?? "";
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return "root";
  if (segments.length === 1) return segments[0]!;
  // Keep at most two segments — deeper paths (e.g.
  // /sectors/Technology/Software) collapse to the area + entity,
  // which matches the schema's ``screen`` convention (``<area>/<entity>``).
  return `${segments[0]}/${segments[1]}`;
}

interface ObservabilityErrorBoundaryProps {
  children: ReactNode;
}

interface ObservabilityErrorBoundaryState {
  errored: boolean;
}

export class ObservabilityErrorBoundary extends Component<
  ObservabilityErrorBoundaryProps,
  ObservabilityErrorBoundaryState
> {
  override state: ObservabilityErrorBoundaryState = { errored: false };

  static getDerivedStateFromError(): ObservabilityErrorBoundaryState {
    return { errored: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const screen = deriveScreen();
    const feature = screen.split("/")[0] ?? "unknown";

    // ── PostHog Exceptions (stack-trace UI) ────────────
    // Wrapped in try/catch so a PostHog hiccup doesn't replace the
    // React error with a different one. The redactor (before_send)
    // strips query strings and forbidden properties before send.
    try {
      posthog.captureException(error, {
        extra: {
          screen,
          feature,
          componentStack: errorInfo.componentStack,
          error_boundary: true,
        },
      });
    } catch {
      // Posthog is best-effort observability — swallow.
    }

    // ── Event timeline (catalog event) ─────────────────
    try {
      captureEvent({
        event_name: "platform.error.surfaced",
        screen,
        error_message: truncateMessage(error.message),
        error_boundary: true,
        feature,
        ...standardBody(),
      });
    } catch {
      // Same swallow rationale.
    }

    // ── Dev console mirror ─────────────────────────────
    // Production builds skip the console.error because PostHog
    // Exceptions is the source of truth. The dev mirror keeps HMR
    // feedback honest.
    if (process.env.NODE_ENV !== "production") {
      console.error("[ObservabilityErrorBoundary] caught:", error, errorInfo);
    }
  }

  override render(): ReactNode {
    if (this.state.errored) {
      // ``TileError`` is the established design grammar for in-page
      // failures (network 500, tile data missing, etc.). Reusing it
      // keeps the platform's failure UX consistent regardless of the
      // failure source.
      return (
        <TileError
          message="Something went wrong on this page. Try reloading."
          onRetry={() => {
            // Hard reload — the boundary doesn't know whether the
            // failing component is recoverable in-place. A full
            // reload is the safe default; if a regression makes the
            // page un-recoverable the user can still navigate via
            // the platform shell which lives ABOVE this boundary.
            if (typeof window !== "undefined") window.location.reload();
          }}
          retryLabel="Reload page"
        />
      );
    }
    return this.props.children;
  }
}
